import './Sidebar.scss';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../state/hooks';
import { RootState } from '../../../state/store';
import { Tree } from 'primereact/tree';
import TreeNode from 'primereact/treenode';
import { LayerRestApi } from '../../../util/restapi/layer';
import { setExternalLayerExtent, setIsLayerVisible, setProjects, setSelectedLayer, setSelectedLayerGeoserverUrl, setStyles, setVisibleDomains, setWorkspace, setEnabledGroupAlias, setOfflines, setSelectedProjectSwipe } from '../../../state/features/layerSlice';
import { Field, Layer, LayerGroup, Project } from '../../../util/model/project';
import { useTranslation } from 'react-i18next';
import { ContextMenu } from 'primereact/contextmenu';
import { setButtonState, setExportButton, setSwipeButton, setTableButton } from '../../../state/features/buttonStateSlice';
import { FeatureRestApi } from '../../../util/restapi/features';
import { setFeatureExtent, setSelectedFeature } from '../../../state/features/selectedFeatureSlice';
import { setExportLayerAlias, setExportLayerUrl, setExportLayerWorkspace, setShowExport } from '../../../state/features/exportSlice';
import { ButtonState } from '../../shared/button-state/ButtonState';
import { transformExtent } from 'ol/proj';
import { Toast } from 'primereact/toast';
import axios from 'axios';
import WMSCapabilities from 'ol/format/WMSCapabilities';
import { setImportedFileVisibility } from '../../../state/features/importSlice';
import { Filter, GalleryHorizontal } from 'lucide-react';
import { setLayerFilterCql } from '../../../state/features/tableSlice';
import { setSidebarWidth } from '../../../state/features/styleControlSlice';

enum StyleType {
  GENERAL='general',
  DOMAIN='domain',
  GRADUATED='graduated',
  CLASSIFIED='classified'
}

export const Sidebar = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const toast = useRef<any>(null);
  const [nodes, setNodes] = useState<TreeNode[]>();
  const [clickedText, setClickedText] = useState<TreeNode[]>();
  const [wasNodesSet, setWasNotesSet] = useState<boolean>(false)
  const [isExpandedKeysDefaultFlag, setIsExpandedKeysDefaultFlag] = useState<boolean>(true);
  const [expandedKeysArrayFromLocal, setExpandedKeysArrayFromLocal] = useState<any[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<any>();
  const [expandedKeys, setExpandedKeys] = useState({});
  const [collapseButtonVis, setCollapseButtonVis] = useState<boolean>(false);
  const [selectedNodeKey, setSelectedNodeKey] = useState<any>();
  const [contextMenuSelectedItem, setContextMenuSelectedItem] = useState<any>();
  const [contextMenuSelectedItemSwipe, setContextMenuSelectedItemSwipe] = useState<any>();
  const [styles, setUsedStyles] = useState<any>();
  const [findedService, setFindedService] = useState<any>({ type: '', url: 'workspace: ' });
  const services: any = useAppSelector(state => state.layer.services);
  const projects: Project[] = useAppSelector((state: RootState) => state.layer.projects);
  const selectedLayer: any = useAppSelector((state: RootState) => state.layer.selectedLayer);
  const contextMenu: any = useRef(null);
  const contextMenuSwipe: any = useRef(null);
  const visibleLayers: any = localStorage.getItem('sidebarLayersAreOpened')
  const parsedLayers = visibleLayers && visibleLayers.length > 0 ? JSON.parse(visibleLayers) : []
  const importedFile = useAppSelector(state => state.import.importedFile);
  const importLayerGroup: any = useAppSelector(
    (state) => state.import.importLayerGroup
  );
  const [selectedGroupAlias, setSelectedGroupAlias] = useState<any>(null);
  const offlines = useAppSelector(state => state.layer.offlines);
  const layerFilterCql : any = useAppSelector(state => state.table.layerFilterCql);
  const didMount = useRef(false);
  const sidebarRef = useRef<any>();
  const [sidebarKeyCount, setSidebarKeyCount] = useState<string>('key.1')
  const control = useRef(false);

  useEffect(() => {
    if(didMount.current){
      setWasNotesSet(true)
    }
  }, [didMount.current]);

  useEffect(() => {
    if (!sidebarRef.current) return;
    const resizeObserver = new ResizeObserver(() => {
      dispatch(setSidebarWidth(sidebarRef.current.offsetWidth || 0))
    });
    resizeObserver.observe(sidebarRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (wasNodesSet) {
      let arrayFromLocal = []
      const localString = localStorage.getItem('expandedKeysFromLocal')
      if (localString && localString != '') {
        arrayFromLocal = JSON.parse(localString)
      }
      
      setExpandedKeysArrayFromLocal(arrayFromLocal)
    }
  }, [wasNodesSet])

  useEffect(() => {
    if (wasNodesSet) {
      const arrayToProcess = []
      if (nodes) {
        for (let i = 0; i < nodes.length; i++) {
          const node = nodes[i];
          const children = node.children
          for (let index = 0; index < expandedKeysArrayFromLocal.length; index++) {
            const node = expandedKeysArrayFromLocal[index];
            if (node.project_id && !node.layer_group_id && !node.layer_id && !node.layer_name) {
              arrayToProcess.push(node)
            }
          }
          if (children?.length) {
            for (let j = 0; j < children.length; j++) {
              const child = children[j];
              for (let index = 0; index < expandedKeysArrayFromLocal.length; index++) {
                const node = expandedKeysArrayFromLocal[index];
                if (node.project_id && node.layer_group_id && !node.layer_id && !node.layer_name) {
                  arrayToProcess.push(node)
                }
              }
              const grandChildren = child.children
              if (grandChildren?.length) {
                for (let k = 0; k < grandChildren.length; k++) {
                  const grandChild = grandChildren[k];
                  for (let index = 0; index < expandedKeysArrayFromLocal.length; index++) {
                    const node = expandedKeysArrayFromLocal[index];
                    if (node.project_id && node.layer_group_id && node.layer_id && node.layer_name) {
                      arrayToProcess.push(node)
                    }
                  }
                }
              }
            }
          }
        }

        let expandedKeysObject: any = {}
        for (let i = 0; i < arrayToProcess.length; i++) {
          const element = arrayToProcess[i];
          if (element.project_id && !element.layer_group_id && !element.layer_name) {
            for (let i = 0; i < nodes.length; i++) {
              const node = nodes[i] as any;
              if (element.project_id == node.id) {
                expandedKeysObject[node.key] = true
              }
            }
          }
          if (element.project_id && element.layer_group_id && !element.layer_name) {
            for (let i = 0; i < nodes.length; i++) {
              const node = nodes[i] as any;
              for (let j = 0; j < node.children.length; j++) {
                const child = node.children[j];
                if (element.project_id == child.project_id && element.layer_group_id == child.id) {
                  expandedKeysObject[child.key] = true
                }
              }
            }
          }
          if (element.project_id && element.layer_group_id && element.layer_name) {
            for (let i = 0; i < nodes.length; i++) {
              const node = nodes[i] as any;
              for (let j = 0; j < node.children.length; j++) {
                const child = node.children[j];

                for (let k = 0; k < child.children.length; k++) {
                  const grandChild = child.children[k];
                  if (element.project_id == child.project_id && element.layer_group_id == child.layer_group_id && element.layer_name == child.name) {
                    expandedKeysObject[grandChild.key] = true
                  }
                }
              }
            }
          }
        }
        setIsExpandedKeysDefaultFlag(false)
        setExpandedKeys(expandedKeysObject)
      }
    }

  }, [expandedKeysArrayFromLocal])

  useEffect(() => {
    if (contextMenuSelectedItem) {
      const findedService = services.find((service: any) => { return service.id === contextMenuSelectedItem.service_id })
      if (findedService) {
        setFindedService({ type: findedService.type, url: findedService.url, workspace: findedService.workspace })
      }
    }
  }, [services, contextMenuSelectedItem])

  const contextMenuItemsSwipe = [
    {
      label: t('CONTEXT MENU.Open Swipe'),
      command: () => {
          const clickedNodeName = contextMenuSelectedItemSwipe.id
          // setClickedText(clickedNodeName)
          nodes?.map((node: any) => {
            if (node.id === clickedNodeName) {
              dispatch(setSelectedProjectSwipe(node))
              dispatch(setButtonState(ButtonState.SWIPE))
              console.log(" dispatch(setSwipeButton(true))");
              
              dispatch(setSwipeButton(true))
            }
          })
      }
    }
  ];

  const contextMenuItems = [
    findedService.type !== 'EXTERNAL_LAYER' && findedService.type !== 'RASTER' ? {
      label: t('CONTEXT MENU.Go to the Layer'),
      command: (e: any) => {
        const url = services.find((service: any) => service.id === contextMenuSelectedItem.service_id)?.url
        dispatch(setSelectedLayer(contextMenuSelectedItem));
        dispatch(setSelectedLayerGeoserverUrl(url));
        getFeatures(url);
      }
    } : {
      label: t('CONTEXT MENU.Go to the Layer'),
      command: () => {
        goToExternalLayer(contextMenuSelectedItem);
      }
    },
    findedService.type !== 'EXTERNAL_LAYER' && findedService.type !== 'RASTER' ? {
      label: t('CONTEXT MENU.Open/Close Table'),
      command: () => {
        dispatch(setSelectedFeature([]))
        const url = services.find((service: any) => service.id === contextMenuSelectedItem.service_id)?.url
        const workspace = services.find((service: any) => service.id === contextMenuSelectedItem.service_id).workspace;
        dispatch(setSelectedLayer(contextMenuSelectedItem));
        dispatch(setSelectedLayerGeoserverUrl(url));
        dispatch(setWorkspace(workspace));
        dispatch(setTableButton(true))
      }
    } : {
      className: "hidden-context-menu",
    },
    {
      label: t('CONTEXT MENU.Export'),
      command: () => {
        const clickedNodeName = contextMenuSelectedItem.alias
        setClickedText(clickedNodeName)

        nodes?.map((node: any) => {
          if (node.children) {
            node.children.map((child: any) => {
              child.children.find((child2: any) => {
                if (child2.alias === clickedNodeName) {
                  if (child2) {
                    const url = services.find((service: any) => service.id === child2.service_id)?.url
                    const workspace = services.find((service: any) => service.id === child2.service_id).workspace;
                    dispatch(setSelectedLayerGeoserverUrl(url));
                    dispatch(setWorkspace(workspace));
                    dispatch(setSelectedLayer(child2))

                    dispatch(setExportLayerAlias(child2.alias))
                    dispatch(setExportLayerUrl(url))
                    dispatch(setExportLayerWorkspace(workspace))
                    dispatch(setButtonState(ButtonState.EXPORT))
                    dispatch(setExportButton(true))
                    dispatch(setShowExport(true))
                  }
                }
              })
            })
          }
        })
      }
    },
  ];

  const goToExternalLayer = async (selectedLayer: any) => {
    if (findedService.type === 'EXTERNAL_LAYER') {

      try {
        const xmlCapabilities =
          await axios.get(`${findedService.url}?service=wms&version=1.3.0&request=GetCapabilities`, { responseType: 'text' })

        const wmsCapabilitiesFormat: any = new WMSCapabilities();
        const jsonCapabilities = wmsCapabilitiesFormat.read(xmlCapabilities.data)

        if (jsonCapabilities && jsonCapabilities.Capability
          && jsonCapabilities.Capability.Layer && jsonCapabilities.Capability.Layer.Layer) {
          const layers = jsonCapabilities.Capability.Layer.Layer

          const layerInfo = layers.find((obj: any) => obj.Name === selectedLayer.name)
          if (layerInfo) {

            let extent;
            let transform;
            const geoBBOX = layerInfo.BoundingBox.find((obj: any) => obj.crs === 'CRS:84')
            if (geoBBOX) {
              extent = geoBBOX.extent;
              transform = transformExtent(extent, 'EPSG:4326', 'EPSG:3857');
            } else {
              const bbox = layerInfo.BoundingBox[0]
              if (bbox) {
                extent = transformExtent(bbox.extent, bbox.crs, 'EPSG:4326');
                transform = transformExtent(extent, 'EPSG:4326', 'EPSG:3857');
              } else {
                toast.current?.show({ severity: 'error', detail: "Katman BBOX'u Bulamıyorum", life: 5000 });
              }
            }
            dispatch(setExternalLayerExtent(transform))

            return { extent }
          } else {
            toast.current?.show({ severity: 'error', detail: 'Hizmette Katman Bulunamıyor', life: 5000 });
          }
        } else {
          toast.current?.show({ severity: 'error', detail: 'Hizmet Yetenekleri Ayrıştırılamıyor', life: 5000 });
        }
      } catch (error) {
        toast.current?.show({ severity: 'error', detail: 'Hizmet Yetenekleri Alınamıyor', life: 5000 });
        console.warn(error);
      }
    }
  }

  useEffect(() => {
    if (services && services.length > 0) {
      const promiseArray: any[] = [];
      const res: any = [];
      projects.map((project: Project) => {
        project.layer_groups.map((layerGroup: LayerGroup) => {
          layerGroup.layers.map((layer: Layer) => {
            const type = services.find((service: any) => service.id === layer.service_id)?.type
            if (type === 'RASTER' || type === 'EXTERNAL_LAYER') {
              return
            }
            else {
              const response = LayerRestApi.getStyleWithLayername(layer.name);
              promiseArray.push(response)
            }
          })
        })
      })

      Promise.all(promiseArray).then((responseArray: any) => {
        if (responseArray) {
          responseArray.map((response: any) => {
            if (response) {
              res.push(...response.data.style)
            }
          })
          setUsedStyles(res)
          /**
           * kontrol edilmeli
           */
          dispatch(setStyles(res))
        }
      })
      /**
      * kontrol edilmeli
      */
      // dispatch(setStyles(styles))
    }
  }, [projects, services])


  useEffect(() => {
    if (services && services.length > 0) {

      const visibleLayers = parsedLayers.filter((el: any) => el.visible === true)

      const partials = parsedLayers.filter((el: any) => el.partial === true)

      const obj = { ...selectedKeys };

      visibleLayers.forEach((layer: any) => {
        obj[layer.name] = {
          checked: true,
          partialChecked: false,
        };

      });

      partials.forEach((layer: any) => {
        if (layer.name.includes('/')) {
          obj[layer.name] = {
            checked: false,
            partialChecked: true,
          };
        } else {
          obj[layer.name] = {
            checked: false,
            partialChecked: true,
          };
        }

      });

      if (offlines.length) {
        offlines.forEach((offline: any) => {
          obj[offline.key] = {
            checked: true,
            partialChecked: false,
          }
        })
      }

      setSelectedKeys(obj)
    }

  }, [services, offlines]);

  useEffect(() => {
    if (!isExpandedKeysDefaultFlag) {
      const theArray = []
      const expandedKeysKeys = Object.keys(expandedKeys)
      if (expandedKeysKeys?.length) {
        for (let i = 0; i < expandedKeysKeys.length; i++) {
          const key = expandedKeysKeys[i];
          if (nodes?.length) {
            for (let j = 0; j < nodes.length; j++) {
              const node = nodes[j] as any;
              if (node.key == key) {
                theArray.push({ project_id: node.id })
              }
              const children = node.children
              if (children?.length) {
                for (let k = 0; k < node.children.length; k++) {
                  const child = node.children[k];
                  if (child.key == key) {
                    theArray.push({ project_id: node.id, layer_group_id: child.id })
                  }
                  const grandChildren = child.children
                  if (grandChildren.length) {
                    for (let l = 0; l < grandChildren.length; l++) {
                      const grandChild = grandChildren[l];
                      if (grandChild.key == key) {
                        theArray.push({ project_id: node.id, layer_group_id: child.id, layer_id: grandChild.id, layer_name: grandChild.name })
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }

      localStorage.setItem('expandedKeysFromLocal', JSON.stringify(theArray))
    }
  }, [expandedKeys])

  useEffect(() => {
    let keyCount = sidebarKeyCount
    if (styles && styles.length > 0) {

      let data: any[] = [];
      let mainData: any[] = [];
      let childrenData: any[] = [];
      let domainData: any[] = [];
      const classes = document.createElement('style');
      classes.innerHTML = ``;

      // dispatch(setStyles(styles));
      projects.map((project: Project) => {
        mainData = []

        const test = project.layer_groups.map((layerGroup: LayerGroup) => {
          if (layerGroup.layers.length > 0) {
            childrenData = [];
            layerGroup.layers.find((layer: any) => {

            //  const style = styles.find((style: any) => style.layer_id === layer.id);
              const style = styles.filter((style: any) => style.layer_id === layer.id);
              
              // console.log("style ",style);

              let domainLayers;
              if (style[0]) {
                domainLayers = layer.fields.filter((field: Field) => field.type === 'Domain' && field.id === style[0].field_id);
                if(domainLayers.length){
       /*            console.log("layer :",layer); */
                }
          /*       console.log("domainLayers");
                console.log("style cate ",style.cate);*/
         
              }
   /*            if(style[0] && style[0].cate){
                console.log("style cate ",style.cate);
                console.log("stylee ",style); 
              } */
              if (style[0]) {
                switch (style[0].type) {
                  case 'point':
                    if (style[0].icon) {
                      //domainli layer değilse
                      if (false/* layer.layer_group_id === layerGroup.id && domainLayers?.length > 0 */) {
                        //  console.log("domainli layer değilse");
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d')!;
                        canvas.width = 20
                        canvas.height = 20
                        ctx.beginPath();
                        ctx.fillStyle = '#ffffff'
                        ctx.arc(10, 10, 8, 0, 2 * Math.PI);
                        ctx.fill();
                        const dataURL = canvas.toDataURL();
                        const classPoint = `\n.iconforsidebar_${layer.name} { background-image: url(${dataURL}); min-width: 17px;} }`
                        classes.innerHTML += ` ${classPoint}`

                      }
                      //domainli layersa
                      else {
                /*         console.log("domainli layersa");
                        console.log("style cate ",style.cate);
                        console.log("stylee ",style); */

                        classes.innerHTML += `\n.iconforsidebar_${layer.name} { background-image: url(data:image/${style[0].icon}) }`

                      }
                    }
                    else {
                      const lineCanvas = document.createElement('canvas');
                      const ctx = lineCanvas.getContext('2d')!;
                      if (style[0].shape === 'circle') {
                        lineCanvas.width = 20
                        lineCanvas.height = 20
                        ctx.beginPath();
                        ctx.fillStyle = layer.layer_group_id === layerGroup.id && domainLayers?.length > 0 ? '#ffffff' : style[0].fill_color;
                        ctx.arc(10, 10, 8, 0, 2 * Math.PI);
                        ctx.fill();
                        const dataURL = lineCanvas.toDataURL();
                        const classPoint = `\n.iconforsidebar_${layer.name} { background-image: url(${dataURL}); min-width: 17px; }`
                        classes.innerHTML += ` ${classPoint}`
                      }
                      if (style[0].shape === 'triangle') {
                        lineCanvas.width = 100
                        lineCanvas.height = 100
                        ctx.beginPath();
                        ctx.moveTo(50, 0);
                        ctx.lineTo(0, 100);
                        ctx.lineTo(100, 100);
                        ctx.closePath();
                        ctx.fillStyle = layer.layer_group_id === layerGroup.id && domainLayers?.length > 0 ? '#ffffff' : style[0].fill_color;
                        ctx.lineWidth = style[0].stroke_width
                        ctx.fillStyle = layer.layer_group_id === layerGroup.id && domainLayers?.length > 0 ? '#ffffff' : style[0].fill_color;
                        ctx.strokeStyle = layer.layer_group_id === layerGroup.id && domainLayers?.length > 0 ? '#ffffff' : style[0].stroke_color;
                        ctx.fill();
                        const dataURL = lineCanvas.toDataURL();
                        const classPoint2 = `\n.iconforsidebar_${layer.name} { background-image: url(${dataURL}) }`
                        classes.innerHTML += ` ${classPoint2}`
                      }
                      if (style[0].shape === 'square') {
                        lineCanvas.width = 100
                        lineCanvas.height = 100
                        ctx.clearRect(0, 0, 100, 100);
                        ctx.beginPath();
                        ctx.lineWidth = style[0].stroke_width;
                        ctx.strokeRect(0, 0, 100, 100);
                        ctx.rect(0, 0, 100, 100);
                        ctx.fillStyle = layer.layer_group_id === layerGroup.id && domainLayers?.length > 0 ? '#ffffff' : style[0].fill_color;
                        ctx.strokeStyle = layer.layer_group_id === layerGroup.id && domainLayers?.length > 0 ? '#ffffff' : style[0].stroke_color;
                        ctx.closePath();
                        ctx.fill();
                        ctx.stroke();
                        const dataURL = lineCanvas.toDataURL();
                        const classPoint2 = `\n.iconforsidebar_${layer.name} { background-image: url(${dataURL}) }`
                        classes.innerHTML += ` ${classPoint2}`
                      }
                    }
                    break;
                  case 'linestring':
                    const lineCanvas = document.createElement('canvas');
                    const lineCtx = lineCanvas.getContext('2d')!;
                    lineCanvas.width = 20
                    lineCanvas.height = 20
                    lineCtx.moveTo(0, 10);
                    lineCtx.lineTo(20, 10);
                    lineCtx.lineWidth = style[0].stroke_width;
                    lineCtx.strokeStyle = layer.layer_group_id === layerGroup.id && domainLayers?.length > 0 ? '#ffffff' : style[0].stroke_color;
                    lineCtx.stroke();
                    const dataURL = lineCanvas.toDataURL();
                    const classString = `\n.iconforsidebar_${layer.name} { background-image: url(${dataURL}) }`
                    classes.innerHTML += ` ${classString}`
                    break;
                  case 'polygon':
                    const polygonCanvas = document.createElement('canvas');
                    polygonCanvas.width = 100
                    polygonCanvas.height = 100
                    const polygonCtx = polygonCanvas.getContext('2d')!;
                    polygonCtx.fillStyle = layer.layer_group_id === layerGroup.id && domainLayers?.length > 0 ? '#ffffff' : style[0].fill_color;
                    polygonCtx.strokeStyle = layer.layer_group_id === layerGroup.id && domainLayers?.length > 0 ? '#ffffff' : style[0].stroke_color
                    polygonCtx.lineWidth = style[0].stroke_width;
                    polygonCtx.beginPath();
                    polygonCtx.moveTo(50 + 50 * Math.cos(0), 50 + 50 * Math.sin(0));
                    for (let i = 1; i <= 5; i += 1) {
                      polygonCtx.lineTo(50 + 50 * Math.cos(i * 2 * Math.PI / 5), 50 + 50 * Math.sin(i * 2 * Math.PI / 5));
                    }
                    polygonCtx.lineWidth = 6;
                    polygonCtx.stroke();
                    polygonCtx.closePath();
                    polygonCtx.fill();
                    const dataURLPolygon = polygonCanvas.toDataURL();
                    const classStringPolygon = `\n.iconforsidebar_${layer.name} { background-image: url(${dataURLPolygon}) }`
                    classes.innerHTML += ` ${classStringPolygon}`
                    break;
                  default:
                    break;
                }
                //domain icon için burdan devam
                if (domainLayers.length > 0) {

                /*  console.log("domain icon için burdan devam",domainLayers);
                  console.log("style cate ",style[0].cate);
                  console.log("stylee ",style);  */
                  
                  domainLayers.map((domainLayer: any) => {
                    if (layer.layer_group_id === layerGroup.id) {
                      let newChild = {
                        ...layer,
                        key: layer.name,
                        label: layer.alias,
                        icon: `iconforsidebar_${layer.name}`
                      }
                      //  layerlara iconlarıyla ekle
                      if (layer.id === domainLayer.layer_id) {
                        const styleArray = styles.filter((style: any) => style.layer_id === layer.id);
/*                         console.log("styleArray ",styleArray);
                        console.log("domainLayer ",domainLayer);
                         */
                        styleArray.map((style: any, index:number) => {
                          const newArr = domainLayer.domain ? domainLayer.domain.map((dom: any) => {
                            if (style.value_id === dom.id) {

                              switch (style.type) {
                                case 'point':
                                  if (style.icon) {
                                    classes.innerHTML += `\n.iconforsidebar_${layer.id}${dom.id} { background-image: url(${style.icon}) }`
                                  }
                                  else {
                                    const lineCanvas = document.createElement('canvas');
                                    const ctx = lineCanvas.getContext('2d')!;
                                    if (style.shape === 'circle') {
                                      lineCanvas.width = 20
                                      lineCanvas.height = 20
                                      ctx.beginPath();
                                      ctx.fillStyle = style.fill_color
                                      ctx.arc(10, 10, 8, 0, 2 * Math.PI);
                                      ctx.fill();
                                      const dataURL = lineCanvas.toDataURL();
                                      const classPoint = `\n.iconforsidebar_${layer.id}${dom.id} { background-image: url(${dataURL}) }`
                                      classes.innerHTML += ` ${classPoint}`
                                    }
                                    if (style.shape === 'triangle') {
                                      lineCanvas.width = 100
                                      lineCanvas.height = 100
                                      ctx.beginPath();
                                      ctx.moveTo(50, 0);
                                      ctx.lineTo(0, 100);
                                      ctx.lineTo(100, 100);
                                      ctx.closePath();
                                      ctx.fillStyle = style.fill_color
                                      ctx.lineWidth = style.stroke_width
                                      ctx.fillStyle = style.fill_color;
                                      ctx.strokeStyle = style.stroke_color;
                                      ctx.fill();
                                      const dataURL = lineCanvas.toDataURL();
                                      const classPoint2 = `\n.iconforsidebar_${layer.id}${dom.id} { background-image: url(${dataURL}) }`
                                      classes.innerHTML += ` ${classPoint2}`
                                    }
                                    if (style.shape === 'square') {
                                      lineCanvas.width = 100
                                      lineCanvas.height = 100
                                      ctx.clearRect(0, 0, 100, 100);
                                      ctx.beginPath();
                                      ctx.lineWidth = style.stroke_width;
                                      ctx.strokeRect(0, 0, 100, 100);
                                      ctx.rect(0, 0, 100, 100);
                                      ctx.fillStyle = style.fill_color;
                                      ctx.strokeStyle = style.stroke_color;
                                      ctx.closePath();
                                      ctx.fill();
                                      ctx.stroke();
                                      const dataURL = lineCanvas.toDataURL();
                                      const classPoint2 = `\n.iconforsidebar_${layer.id}${dom.id} { background-image: url(${dataURL}) }`
                                      classes.innerHTML += ` ${classPoint2}`
                                    }
                                  }
                                  break;
                                case 'linestring':
                                  const lineCanvas = document.createElement('canvas');
                                  lineCanvas.width = 20
                                  lineCanvas.height = 20
                                  const ctx = lineCanvas.getContext('2d')!;
                                  ctx.moveTo(0, 10);
                                  ctx.lineTo(20, 10);
                                  ctx.lineWidth = style.stroke_width;
                                  ctx.strokeStyle = style.stroke_color;
                                  ctx.stroke();
                                  const dataURL = lineCanvas.toDataURL();
                                  const classString = `\n.iconforsidebar_${layer.id}${dom.id} { background-image: url(${dataURL}) }`
                                  classes.innerHTML += ` ${classString}`
                                  break;
                                case 'polygon':
                                  const polygonCanvas = document.createElement('canvas');
                                  polygonCanvas.width = 100
                                  polygonCanvas.height = 100
                                  const polygonCtx = polygonCanvas.getContext('2d')!;
                                  polygonCtx.fillStyle = style.fill_color;
                                  polygonCtx.strokeStyle = style.stroke_color;
                                  polygonCtx.lineWidth = style.stroke_width;
                                  polygonCtx.beginPath();
                                  polygonCtx.moveTo(50 + 50 * Math.cos(0), 50 + 50 * Math.sin(0));
                                  for (let i = 1; i <= 5; i += 1) {
                                    polygonCtx.lineTo(50 + 50 * Math.cos(i * 2 * Math.PI / 5), 50 + 50 * Math.sin(i * 2 * Math.PI / 5));
                                  }
                                  polygonCtx.lineWidth = 6;
                                  polygonCtx.stroke();
                                  polygonCtx.closePath();
                                  polygonCtx.fill();

                                  const dataURLPolygon = polygonCanvas.toDataURL();
                                  const classStringPolygon = `\n.iconforsidebar_${layer.id}${dom.id} { background-image: url(${dataURLPolygon}) }`
                                  classes.innerHTML += ` ${classStringPolygon}`
                                  break;
                                default:
                                  break;
                              }
                            }
                            const fieldName = layer.fields.find((field: Field) => field.id === style.field_id)?.name
                            return { ...dom, key: 'domain/' + fieldName + '/' + dom.id + '/' + layer.name, label: dom.desc, icon: `iconforsidebar_${layer.id}${dom.id}` }
                          }) : [];

                          if (index === styleArray.length - 1) {
                            const unAssignedStyle = styleArray.find((el: any) => el.value_id === -1);
                            const fieldName = layer.fields.find((field: Field) => field.id === style.field_id)?.name
                            if (unAssignedStyle) {
                                const dataURLShape = generateUnassignedStyle(unAssignedStyle);
                                newArr.push({
                                  id: unAssignedStyle.value_id,
                                  desc: unAssignedStyle.value_desc,
                                  key: `domain/${fieldName}/-1/${layer.name}`,
                                  icon: `iconforsidebar_${layer.id}-1`,
                                  label: unAssignedStyle.value_desc
                                });
                                classes.innerHTML += `\n.iconforsidebar_${layer.id}-1 { background-image: url(${dataURLShape}) }`;
                            }
                          }
                          // const sortedNewArr = sortByPriority(newArr);
                          newChild = {
                            ...newChild,
                            children: newArr
                          }
                        })
                      }
                      childrenData.push(newChild);
                    }
                  })
                }
                else {
                  if(style[0].cate === StyleType.CLASSIFIED){
/*                      console.log("else : ");
                        console.log("layer :",layer);
                        console.log("stylee ",style); 
                        console.log("style cate ",style[0].cate); */

                }
                  if(style[0].cate === StyleType.CLASSIFIED){
                    if (layer.layer_group_id === layerGroup.id) {
                      let newChild = {
                        ...layer,
                        key: layer.name,
                        label: layer.alias,
                        icon: `iconforsidebar_${layer.name}`
                      }
                      //  layerlara iconlarıyla ekle
                        const styleArray = style;
                        
                        const newArr = styleArray.map((style: any) => {
                          let classifiedValue;
                          if(style.classified_value_type==="text"){
                            classifiedValue=style.classified_text_value || 'Boş'; // ? null
                          }else{
                            classifiedValue=style.classified_numeric_value || 'Boş'; // ? null
                          }

                          switch (style.type) {
                            case 'point':
                              if (style.icon) {
                                classes.innerHTML += `\n.iconforsidebar_${layer.id}${style.id} { background-image: url(${style.icon}) }`
                              }
                              else {
                                const lineCanvas = document.createElement('canvas');
                                const ctx = lineCanvas.getContext('2d')!;
                                if (style.shape === 'circle') {
                                  lineCanvas.width = 20
                                  lineCanvas.height = 20
                                  ctx.beginPath();
                                  ctx.fillStyle = style.fill_color
                                  ctx.arc(10, 10, 8, 0, 2 * Math.PI);
                                  ctx.fill();
                                  const dataURL = lineCanvas.toDataURL();
                                  const classPoint = `\n.iconforsidebar_${layer.id}${style.id} { background-image: url(${dataURL}) }`
                                  classes.innerHTML += ` ${classPoint}`
                                }
                                if (style.shape === 'triangle') {
                                  lineCanvas.width = 100
                                  lineCanvas.height = 100
                                  ctx.beginPath();
                                  ctx.moveTo(50, 0);
                                  ctx.lineTo(0, 100);
                                  ctx.lineTo(100, 100);
                                  ctx.closePath();
                                  ctx.fillStyle = style.fill_color
                                  ctx.lineWidth = style.stroke_width
                                  ctx.fillStyle = style.fill_color;
                                  ctx.strokeStyle = style.stroke_color;
                                  ctx.fill();
                                  const dataURL = lineCanvas.toDataURL();
                                  const classPoint2 = `\n.iconforsidebar_${layer.id}${style.id} { background-image: url(${dataURL}) }`
                                  classes.innerHTML += ` ${classPoint2}`
                                }
                                if (style.shape === 'square') {
                                  lineCanvas.width = 100
                                  lineCanvas.height = 100
                                  ctx.clearRect(0, 0, 100, 100);
                                  ctx.beginPath();
                                  ctx.lineWidth = style.stroke_width;
                                  ctx.strokeRect(0, 0, 100, 100);
                                  ctx.rect(0, 0, 100, 100);
                                  ctx.fillStyle = style.fill_color;
                                  ctx.strokeStyle = style.stroke_color;
                                  ctx.closePath();
                                  ctx.fill();
                                  ctx.stroke();
                                  const dataURL = lineCanvas.toDataURL();
                                  const classPoint2 = `\n.iconforsidebar_${layer.id}${style.id} { background-image: url(${dataURL}) }`
                                  classes.innerHTML += ` ${classPoint2}`
                                }
                              }
                              break;
                            case 'linestring':
                              const lineCanvas = document.createElement('canvas');
                              lineCanvas.width = 20
                              lineCanvas.height = 20
                              const ctx = lineCanvas.getContext('2d')!;
                              ctx.moveTo(0, 10);
                              ctx.lineTo(20, 10);
                              ctx.lineWidth = style.stroke_width;
                              ctx.strokeStyle = style.stroke_color;
                              ctx.stroke();
                              const dataURL = lineCanvas.toDataURL();
                              const classString = `\n.iconforsidebar_${layer.id}${style.id} { background-image: url(${dataURL}) }`
                              classes.innerHTML += ` ${classString}`
                              break;
                            case 'polygon':
                              const polygonCanvas = document.createElement('canvas');
                              polygonCanvas.width = 100
                              polygonCanvas.height = 100
                              const polygonCtx = polygonCanvas.getContext('2d')!;
                              polygonCtx.fillStyle = style.fill_color;
                              polygonCtx.strokeStyle = style.stroke_color;
                              polygonCtx.lineWidth = style.stroke_width;
                              polygonCtx.beginPath();
                              polygonCtx.moveTo(50 + 50 * Math.cos(0), 50 + 50 * Math.sin(0));
                              for (let i = 1; i <= 5; i += 1) {
                                polygonCtx.lineTo(50 + 50 * Math.cos(i * 2 * Math.PI / 5), 50 + 50 * Math.sin(i * 2 * Math.PI / 5));
                              }
                              polygonCtx.lineWidth = 6;
                              polygonCtx.stroke();
                              polygonCtx.closePath();
                              polygonCtx.fill();

                              const dataURLPolygon = polygonCanvas.toDataURL();
                              const classStringPolygon = `\n.iconforsidebar_${layer.id}${style.id} { background-image: url(${dataURLPolygon}) }`
                              classes.innerHTML += ` ${classStringPolygon}`
                              break;
                            default:
                              break;
                          }
                        
                        const fieldName = layer.fields.find((field: Field) => field.id === style.field_id)?.name 
    /*                 
                        console.log(style.classified_value_type === "text"); */
                        //  style.classified_value_type === "text" ? `'${classifiedValue}'` : classifiedValue  
                        const classifiedKey=style.classified_value_type === "text" ? `'` + `${classifiedValue}` + `'` : classifiedValue
                        return { id:style.id, key: 'classified/' + fieldName + '/' + classifiedKey +  '/' + layer.name, label: /* dom.desc */classifiedValue, icon: `iconforsidebar_${layer.id}${style.id}` }

                      //  return { ...dom, key: 'domain/' + fieldName + '/' + dom.id + '/' + layer.name, label: dom.desc, icon: `iconforsidebar_${layer.id}${dom.id}` }
                      
                        })
                        // const sortedNewArr = sortByPriority(newArr);
                        newChild = {
                          ...newChild,
                          children: newArr
                        }

                      childrenData.push(newChild);
                    }
                  }else if(style[0].cate === StyleType.GRADUATED){
                    if (layer.layer_group_id === layerGroup.id) {
                      let newChild = {
                        ...layer,
                        key: layer.name,
                        label: layer.alias,
                        icon: `iconforsidebar_${layer.name}`
                      }
                      //  layerlara iconlarıyla ekle
                        const styleArray = style;
                        
                        const newArr = styleArray.map((style: any) => {

                          let graduatedVal = (style.graduated_min || 'Boş') + ' - ' + (style.graduated_max || 'Boş');

                          switch (style.type) {
                            case 'point':
                              if (style.icon) {
                                classes.innerHTML += `\n.iconforsidebar_${layer.id}${style.id} { background-image: url(${style.icon}) }`
                              }
                              else {
                                const lineCanvas = document.createElement('canvas');
                                const ctx = lineCanvas.getContext('2d')!;
                                if (style.shape === 'circle') {
                                  lineCanvas.width = 20
                                  lineCanvas.height = 20
                                  ctx.beginPath();
                                  ctx.fillStyle = style.fill_color
                                  ctx.arc(10, 10, 8, 0, 2 * Math.PI);
                                  ctx.fill();
                                  const dataURL = lineCanvas.toDataURL();
                                  const classPoint = `\n.iconforsidebar_${layer.id}${style.id} { background-image: url(${dataURL}) }`
                                  classes.innerHTML += ` ${classPoint}`
                                }
                                if (style.shape === 'triangle') {
                                  lineCanvas.width = 100
                                  lineCanvas.height = 100
                                  ctx.beginPath();
                                  ctx.moveTo(50, 0);
                                  ctx.lineTo(0, 100);
                                  ctx.lineTo(100, 100);
                                  ctx.closePath();
                                  ctx.fillStyle = style.fill_color
                                  ctx.lineWidth = style.stroke_width
                                  ctx.fillStyle = style.fill_color;
                                  ctx.strokeStyle = style.stroke_color;
                                  ctx.fill();
                                  const dataURL = lineCanvas.toDataURL();
                                  const classPoint2 = `\n.iconforsidebar_${layer.id}${style.id} { background-image: url(${dataURL}) }`
                                  classes.innerHTML += ` ${classPoint2}`
                                }
                                if (style.shape === 'square') {
                                  lineCanvas.width = 100
                                  lineCanvas.height = 100
                                  ctx.clearRect(0, 0, 100, 100);
                                  ctx.beginPath();
                                  ctx.lineWidth = style.stroke_width;
                                  ctx.strokeRect(0, 0, 100, 100);
                                  ctx.rect(0, 0, 100, 100);
                                  ctx.fillStyle = style.fill_color;
                                  ctx.strokeStyle = style.stroke_color;
                                  ctx.closePath();
                                  ctx.fill();
                                  ctx.stroke();
                                  const dataURL = lineCanvas.toDataURL();
                                  const classPoint2 = `\n.iconforsidebar_${layer.id}${style.id} { background-image: url(${dataURL}) }`
                                  classes.innerHTML += ` ${classPoint2}`
                                }
                              }
                              break;
                            case 'linestring':
                              const lineCanvas = document.createElement('canvas');
                              lineCanvas.width = 20
                              lineCanvas.height = 20
                              const ctx = lineCanvas.getContext('2d')!;
                              ctx.moveTo(0, 10);
                              ctx.lineTo(20, 10);
                              ctx.lineWidth = style.stroke_width;
                              ctx.strokeStyle = style.stroke_color;
                              ctx.stroke();
                              const dataURL = lineCanvas.toDataURL();
                              const classString = `\n.iconforsidebar_${layer.id}${style.id} { background-image: url(${dataURL}) }`
                              classes.innerHTML += ` ${classString}`
                              break;
                            case 'polygon':
                              const polygonCanvas = document.createElement('canvas');
                              polygonCanvas.width = 100
                              polygonCanvas.height = 100
                              const polygonCtx = polygonCanvas.getContext('2d')!;
                              polygonCtx.fillStyle = style.fill_color;
                              polygonCtx.strokeStyle = style.stroke_color;
                              polygonCtx.lineWidth = style.stroke_width;
                              polygonCtx.beginPath();
                              polygonCtx.moveTo(50 + 50 * Math.cos(0), 50 + 50 * Math.sin(0));
                              for (let i = 1; i <= 5; i += 1) {
                                polygonCtx.lineTo(50 + 50 * Math.cos(i * 2 * Math.PI / 5), 50 + 50 * Math.sin(i * 2 * Math.PI / 5));
                              }
                              polygonCtx.lineWidth = 6;
                              polygonCtx.stroke();
                              polygonCtx.closePath();
                              polygonCtx.fill();

                              const dataURLPolygon = polygonCanvas.toDataURL();
                              const classStringPolygon = `\n.iconforsidebar_${layer.id}${style.id} { background-image: url(${dataURLPolygon}) }`
                              classes.innerHTML += ` ${classStringPolygon}`
                              break;
                            default:
                              break;
                          }
                        
                        const fieldName = layer.fields.find((field: Field) => field.id === style.field_id)?.name
                        return { id:style.id, key: 'graduated/' + fieldName + '/' + /* dom.id */ graduatedVal + '/' + layer.name, label: /* dom.desc */graduatedVal, icon: `iconforsidebar_${layer.id}${style.id}` }

                      //  return { ...dom, key: 'domain/' + fieldName + '/' + dom.id + '/' + layer.name, label: dom.desc, icon: `iconforsidebar_${layer.id}${dom.id}` }
                      
                        })
                        // const sortedNewArr = sortByPriority(newArr);
                        newChild = {
                          ...newChild,
                          children: newArr
                        }

                      childrenData.push(newChild);
                    }
                  }
                  else if (layer.layer_group_id === layerGroup.id) {
                    if (layer.layer_group_id === layerGroup.id) {
                      let newChild = {
                        ...layer,
                        key: layer.name,
                        label: layer.alias,
                        icon: `iconforsidebar_${layer.name}`
                      }
                      childrenData.push(newChild);
                    }
                  }
                }
              }
              else {
                const type = services.find((service: any) => service.id === layer.service_id)?.type
                if (type === 'RASTER') {
                  const classPoint = `\n.iconforsidebar_${layer.name} { background-image: url(icons/raster.svg); min-width: 17px; }`
                  classes.innerHTML += ` ${classPoint}`
                  if (layer.layer_group_id === layerGroup.id) {
                    let newChild = {
                      ...layer,
                      key: layer.name,
                      label: layer.alias,
                      icon: `iconforsidebar_${layer.name}`
                    }
                    childrenData.push(newChild);
                  }
                }
                if (type === 'EXTERNAL_LAYER') {
                  switch (layer.geomtype) {
                    case 'MultiPointZ':
                      if (style && style.icon) {
                        //domainli layer değilse
                        if (layer.layer_group_id === layerGroup.id && domainLayers?.length > 0) {
                          const canvas = document.createElement('canvas');
                          const ctx = canvas.getContext('2d')!;
                          canvas.width = 20
                          canvas.height = 20
                          ctx.beginPath();
                          ctx.fillStyle = '#ffffff'
                          ctx.arc(10, 10, 8, 0, 2 * Math.PI);
                          ctx.fill();
                          const dataURL = canvas.toDataURL();
                          const classPoint = `\n.iconforsidebar_${layer.name} { background-image: url(${dataURL}); min-width: 17px;} }`
                          classes.innerHTML += ` ${classPoint}`

                        }
                        //domainli layersa
                        else {
                          classes.innerHTML += `\n.iconforsidebar_${layer.name} { background-image: url(data:image/${style.icon}) }`

                        }
                      }
                      else {
                        const lineCanvas = document.createElement('canvas');
                        const ctx = lineCanvas.getContext('2d')!;
                        // if (style.shape === 'circle') {
                        lineCanvas.width = 20
                        lineCanvas.height = 20
                        ctx.beginPath();
                        ctx.fillStyle = '#ffffff';
                        ctx.arc(10, 10, 8, 0, 2 * Math.PI);
                        ctx.fill();
                        const dataURL = lineCanvas.toDataURL();
                        const classPoint = `\n.iconforsidebar_${layer.name} { background-image: url(${dataURL}); min-width: 17px; }`
                        classes.innerHTML += ` ${classPoint}`
                      }
                      break;
                    case 'MultiLineStringZ':
                      const lineCanvas = document.createElement('canvas');
                      const lineCtx = lineCanvas.getContext('2d')!;
                      lineCanvas.width = 20
                      lineCanvas.height = 20
                      lineCtx.moveTo(0, 10);
                      lineCtx.lineTo(20, 10);
                      lineCtx.lineWidth = 1
                      lineCtx.strokeStyle = '#ffffff'
                      lineCtx.stroke();
                      const dataURL = lineCanvas.toDataURL();
                      const classString = `\n.iconforsidebar_${layer.name} { background-image: url(${dataURL}) }`
                      classes.innerHTML += ` ${classString}`
                      break;
                    case 'MultiPolygonZ':
                      const polygonCanvas = document.createElement('canvas');
                      polygonCanvas.width = 100
                      polygonCanvas.height = 100
                      const polygonCtx = polygonCanvas.getContext('2d')!;
                      polygonCtx.fillStyle = '#ffffff';
                      polygonCtx.strokeStyle = '#ffffff';
                      polygonCtx.lineWidth = 1;
                      polygonCtx.beginPath();
                      polygonCtx.moveTo(50 + 50 * Math.cos(0), 50 + 50 * Math.sin(0));
                      for (let i = 1; i <= 5; i += 1) {
                        polygonCtx.lineTo(50 + 50 * Math.cos(i * 2 * Math.PI / 5), 50 + 50 * Math.sin(i * 2 * Math.PI / 5));
                      }
                      polygonCtx.lineWidth = 6;
                      polygonCtx.stroke();
                      polygonCtx.closePath();
                      polygonCtx.fill();
                      const dataURLPolygon = polygonCanvas.toDataURL();

                      const classStringPolygon = `\n.iconforsidebar_${layer.name} { background-image: url(${dataURLPolygon}) }`
                      classes.innerHTML += ` ${classStringPolygon}`
                      break;
                    default:
                      break;
                  }

                  if (layer.layer_group_id === layerGroup.id) {
                    let newChild = {
                      ...layer,
                      key: layer.name,
                      label: layer.alias,
                      icon: `iconforsidebar_${layer.name}`
                    }
                    childrenData.push(newChild);
                  }
                }
              }
            })
            const sortedChildrenData = sortByPriority(childrenData);
            if (project.id === layerGroup.project_id && childrenData.length > 0) {
              const otherChild = {
                ...layerGroup,
                label: layerGroup.alias,
                key: layerGroup.name,
                children: sortedChildrenData
              }
              keyCount = 'key.' + (parseInt(keyCount.split('.')[1]) + 1).toString()
              mainData.push(otherChild)
            }
          }
        })
        const sortedMainData = sortByPriority(mainData);

        if (mainData.length > 0) {
          const allData: any = {
            ...project,
            label: project.alias,
            key: project.name,
            children: sortedMainData
          }
          keyCount = 'key.' + (parseInt(keyCount.split('.')[1]) + 1).toString()
          if (allData.layer_groups && allData.layer_groups.length > 0) {
            data.push(allData)
          }
        }
      })
      document.getElementsByTagName('head')[0].appendChild(classes);

      const sorted = sortByPriority(data);
      const sortedNodes = [...sorted, ...offlines]

      if (importLayerGroup) {
        const isNotElement = sortedNodes.find((el: any) => el.children[0].children[0].label === importLayerGroup.children[0].label);
        if (!isNotElement) {
          const offlineLayer: any = {
            id: 11111 + importedFile.length,
            name: "cevirimdisi",
            key: `Çevirimdışı - ${offlines.length + 1}`,
            label: `Çevirimdışı - ${offlines.length + 1}`,
            children: [{
              id: importLayerGroup.children[0].id,
              project_id: 1,
              name: 'Çevirimdışı',
              key: `Çevirimdışı - ${offlines.length + 1}`,
              label: `Çevirimdışı - ${offlines.length + 1}`,
              children: [{
                id: importLayerGroup.children[0].id,
                key: `Çevirimdışı - ${offlines.length + 1}`,
                label: importLayerGroup.children[0].label,
                name: "Çevirimdışı2",
                layer_type: "offline",
                features: undefined,
                vector: undefined
              }]
            }]
          }
          sortedNodes.push(offlineLayer)
          dispatch(setOfflines([...offlines, offlineLayer]));
          setSelectedGroupAlias(null)
        }
      }
      setNodes(sortedNodes)

      if(!didMount.current){
        didMount.current = true;
      }else{
        if(control.current){
          setSidebarKeyCount(keyCount)
        }
      }
    }
  }, [styles, importLayerGroup])

  useEffect(() => {
    if (!selectedKeys) {
      return
    }
    const newArray = setLayerVisibility()
    dispatch(setProjects([...newArray]))
    findSelectedDomains()

    if (selectedKeys) {
      const cevrimdisiKeys = Object.keys(selectedKeys).filter(
        (key: any) => selectedGroupAlias === key
      );
      if (cevrimdisiKeys.length > 0) {
        for (const key of cevrimdisiKeys) {
          if (key) {
            dispatch(setImportedFileVisibility(true));
          } else {
            dispatch(setImportedFileVisibility(false));
          }
        }
      } else if (/* Ayrıcaaaa.. */ selectedGroupAlias /* !null */) {
        dispatch(setImportedFileVisibility(false));
      }
    }
  }, [selectedKeys])


  const findSelectedDomains = () => {
   // console.log("selectedKeys :",selectedKeys);
    
    //  return     { id:style.id, key: 'classified/' + fieldName + '/' + /* dom.id */ style.id + '/' + layer.name, label: /* dom.desc */classifiedValue, icon: `iconforsidebar_${layer.id}${style.id}` }
    /*  let classifiedValue;
        if(style.classified_value_type==="text"){
          classifiedValue=style.classified_text_value || 'Boş'; // ? null
        }else{
          classifiedValue=style.classified_numeric_value || 'Boş'; // ? null
        }
     */

     // graduated   return { id:style.id, key: 'graduated/' + fieldName + '/' + /* dom.id */ style.id + '/' + layer.name, label: /* dom.desc */graduatedVal, icon: `iconforsidebar_${layer.id}${style.id}` }
     //let graduatedVal = (style.graduated_min || 'Boş') + ' - ' + (style.graduated_max || 'Boş');
     //
 //    return { id:style.id, key: 'graduated/' + fieldName + '/' + /* dom.id */ style.id + '/' + layer.name, label: /* dom.desc */graduatedVal, icon: `iconforsidebar_${layer.id}${style.id}` }

    const visibleDomains: any = []
    for (const [key] of Object.entries(selectedKeys)) {
      if (key.includes('domain')) {
        const arr = key.split('/')
        visibleDomains.push({ filterTag: arr[1], domainId: arr[2], layername: arr[3] })
      }
      if (key.includes('classified')) {
        const arr = key.split('/')
        visibleDomains.push({ filterTag: arr[1], classifiedId: arr[2], layername: arr[3] })
      }
      if (key.includes('graduated')) {
        const arr = key.split('/')
        visibleDomains.push({ filterTag: arr[1], graduatedId: arr[2], layername: arr[3] })
      }
    }
    dispatch(setVisibleDomains(visibleDomains))
  }

  const setLayerVisibility = () => {
    dispatch(setIsLayerVisible(false))

    const sNodes: any[] = [];
    for (const [key] of Object.entries(selectedKeys)) {
      sNodes.push(key);
    }

    const copyProjects = [...projects]
    const newArray = copyProjects.map((project: Project) => {
      const layerGroups = project.layer_groups.map((layerGroup: LayerGroup) => {
        const layers = layerGroup.layers.map((layer: any) => {

          layer.fields.map((field: any) => {
            if (field.has_domain) {

              field.domain.map((domain: any) => {

                const domainKeyIndex = sNodes.findIndex((node: any) => node.includes(`domain/undefined/${domain.id}/${layer.name}`))

                if (domainKeyIndex !== -1) {

                  const findLayerIndex = parsedLayers.findIndex((el: any) => el.name === `domain/undefined/${domain.id}/${layer.name}`);

                  if (findLayerIndex !== -1) {
                    if (selectedKeys[sNodes[domainKeyIndex]].checked === false) {
                      parsedLayers[findLayerIndex] = { name: `domain/undefined/${domain.id}/${layer.name}`, visible: false, partial: true };
                    } else {
                      const a = `domain/undefined/${domain.id}/${layer.name}`
                      parsedLayers[findLayerIndex] = { name: a, visible: true, partial: false };
                    }
                  } else {
                    if (selectedKeys[sNodes[domainKeyIndex]].checked === false) {
                      parsedLayers.push({ name: `domain/undefined/${domain.id}/${layer.name}`, visible: false, partial: true });
                    } else {
                      parsedLayers.push({ name: `domain/undefined/${domain.id}/${layer.name}`, visible: true, partial: false });
                    }
                  }
                  window.localStorage.setItem('sidebarLayersAreOpened', JSON.stringify(parsedLayers));

                  return {
                    ...domain,
                    visible: true
                  }
                } else {
                  const findLayerIndex = parsedLayers.findIndex((el: any) => el.name === `domain/undefined/${domain.id}/${layer.name}`);
                  if (findLayerIndex !== -1) {
                    parsedLayers[findLayerIndex] = { name: `domain/undefined/${domain.id}/${layer.name}`, visible: false, partial: false };
                  } else {
                    parsedLayers.push({ name: `domain/undefined/${domain.id}/${layer.name}`, visible: false, partial: false });
                  }
                  window.localStorage.setItem('sidebarLayersAreOpened', JSON.stringify(parsedLayers));

                  return {
                    ...domain,
                    visible: false
                  }
                }


              })

            }
          })





          const layerKeyIndex = sNodes.findIndex((node: any) => node === layer.name)
          if (layerKeyIndex !== -1) {
            dispatch(setIsLayerVisible(true))

            const findLayerIndex = parsedLayers.findIndex((el: any) => el.name === layer.name);
            if (findLayerIndex !== -1) {
              if (selectedKeys[sNodes[layerKeyIndex]].checked === false) {
                parsedLayers[findLayerIndex] = { name: layer.name, visible: false, partial: true };
              } else {
                parsedLayers[findLayerIndex] = { name: layer.name, visible: true, partial: false };
              }
            } else {
              if (selectedKeys[sNodes[layerKeyIndex]].checked === false) {
                parsedLayers.push({ name: layer.name, visible: false, partial: true });
              } else {
                parsedLayers.push({ name: layer.name, visible: true, partial: false });
              }
            }
            window.localStorage.setItem('sidebarLayersAreOpened', JSON.stringify(parsedLayers))

            return {
              ...layer,
              visible: true
            }
          } else {
            const findLayerIndex = parsedLayers.findIndex((el: any) => el.name === layer.name);
            if (findLayerIndex !== -1) {
              parsedLayers[findLayerIndex] = { name: layer.name, visible: false, partial: false };
            } else {
              parsedLayers.push({ name: layer.name, visible: false, partial: false });
            }
            window.localStorage.setItem('sidebarLayersAreOpened', JSON.stringify(parsedLayers));

            return {
              ...layer,
              visible: false
            }
          }
        })

        const groupKeyIndex = sNodes.findIndex((node: any) => node === layerGroup.name)
        if (groupKeyIndex !== -1) {
          const findLayerIndex = parsedLayers.findIndex((el: any) => el.name === layerGroup.name);
          if (findLayerIndex !== -1) {
            if (selectedKeys[sNodes[groupKeyIndex]].checked === false) {
              parsedLayers[findLayerIndex] = { name: layerGroup.name, visible: false, partial: true };
            } else {
              parsedLayers[findLayerIndex] = { name: layerGroup.name, visible: true, partial: false };
            }
          } else {
            if (selectedKeys[sNodes[groupKeyIndex]].checked === false) {
              parsedLayers.push({ name: layerGroup.name, visible: false, partial: true });
            } else {
              parsedLayers.push({ name: layerGroup.name, visible: true, partial: false });
            }
          }

        } else {

          const findLayerIndex = parsedLayers.findIndex((el: any) => el.name === layerGroup.name);
          if (findLayerIndex !== -1) {
            parsedLayers[findLayerIndex] = { name: layerGroup.name, visible: false, partial: false };
          } else {
            parsedLayers.push({ name: layerGroup.name, visible: false, partial: false });
          }
        }
        window.localStorage.setItem('sidebarLayersAreOpened', JSON.stringify(parsedLayers));

        return {
          ...layerGroup,
          layers: layers
        }
      })

      const projectKeyIndex = sNodes.findIndex((node: any) => node === project.name)
      if (projectKeyIndex !== -1) {
        const findLayerIndex = parsedLayers.findIndex((el: any) => el.name === project.name);
        if (findLayerIndex !== -1) {
          if (selectedKeys[sNodes[projectKeyIndex]].checked === false) {
            parsedLayers[findLayerIndex] = { name: project.name, visible: false, partial: true };
          } else {
            parsedLayers[findLayerIndex] = { name: project.name, visible: true, partial: false };
          }
        } else {
          if (selectedKeys[sNodes[projectKeyIndex]].checked === false) {
            parsedLayers.push({ name: project.name, visible: false, partial: true });
          } else {
            parsedLayers.push({ name: project.name, visible: true, partial: false });
          }
        }
      } else {
        const findLayerIndex = parsedLayers.findIndex((el: any) => el.name === project.name);
        if (findLayerIndex !== -1) {
          parsedLayers[findLayerIndex] = { name: project.name, visible: false, partial: false };
        } else {
          parsedLayers.push({ name: project.name, visible: false, partial: false });
        }
      }
      window.localStorage.setItem('sidebarLayersAreOpened', JSON.stringify(parsedLayers));

      return {
        ...project,
        layer_groups: layerGroups
      }
    })

    return newArray;
  }


  const spanClicked = (e: any) => {
    const clickedNodeName = e.nativeEvent.srcElement.innerText;
    // setClickedText(clickedNodeName)
    dispatch(setSelectedFeature([]))

    nodes?.map((node: any) => {
      if (node.children) {
        node.children.map((child: any) => {
          child.children.find((child2: any) => {
            if (child2.alias === clickedNodeName) {
              if (child2) {
                const findLayerType = services.find((service: any) => service.id === child2.service_id).type
                const url = services.find((service: any) => service.id === child2.service_id)?.url
                const workspace = services.find((service: any) => service.id === child2.service_id).workspace;

                if (findLayerType !== 'EXTERNAL_LAYER' && findLayerType !== 'RASTER') {
                  dispatch(setSelectedLayerGeoserverUrl(url));
                  dispatch(setWorkspace(workspace));
                  dispatch(setSelectedLayer(child2))

                  dispatch(setExportLayerAlias(child2.alias))
                  dispatch(setExportLayerUrl(url))
                  dispatch(setExportLayerWorkspace(workspace))
                  setClickedText(clickedNodeName)

                } else return
              }
            }
          })
        })
      }
    })
    const sortedNodes = sortByPriority(nodes);
    setNodes(sortedNodes)
    e.stopPropagation();
  }

  const sortByPriority = (array: any) => {
    const sortedArray = array.sort((a: any, b: any) => a.priority - b.priority);
    return sortedArray;
  }

  const getFeatures = async (url: any) => {
    const response = await FeatureRestApi.getFeatures(url, contextMenuSelectedItem.name)

    if (response) {
      dispatch(setFeatureExtent(response.data.bbox));
    }
  }

  const nodeTemplate1 = (node: any, options: any) => {
    let label = <b>{node.label}</b>;
    return (
      <div onClick={(e) => spanClicked(e)} className={`${selectedLayer.id && node.layer_group_id && node.label === clickedText ? 'selected-line' : 'line'} ${!node.layer_group_id ? 'disabled' : ''} ${node.layers ? 'boldGroup' : ''} `}>
        <div className={`label ${node.layer_groups && node.layer_groups.length ? 'boldClass' : ''}`}>{label}</div>
      </div>
    )
  }
  const nodeTemplate = (node: any, options: any)  => {
    let label = <b>{node.label}</b>;
    let icon = <Filter id={node.label} onClick={()=>{
      const _layerFilterCql = new Map();
      for (const [key, value] of layerFilterCql) {
          if(key !== node.name/* selectedLayer.name */){
              _layerFilterCql.set(key, value)
          }
      }
      dispatch(setLayerFilterCql(_layerFilterCql))
    }} color="#fff" size={15} />;
   // console.log("node :",node);
    
    return (
      <div onClick={(e) => spanClicked(e)} className={`${selectedLayer.id && node.layer_group_id && node.label === clickedText ? 'selected-line' : 'line'} ${!node.layer_group_id ? '' : ''} ${node.layers ? 'boldGroup' : ''}`}>
        <div className={`label ${node.layer_groups && node.layer_groups.length ? 'boldClass' : ''}`}>
          {label}
          {node.layer_group_id && layerFilterCql.get(node.name) &&
            <div className="icon-container">{icon}</div>
          }
        </div>
      </div>
    );
  };
  
  const poli = document.createElement('canvas');
  poli.width = 100;
  poli.height = 100;
  const ctx = poli.getContext('2d')!;
  ctx.fillStyle = "#ffffff";
  ctx.strokeStyle = "#ffffff";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(50 + 50 * Math.cos(0), 50 + 50 * Math.sin(0));
  for (let i = 1; i <= 5; i += 1) {
      ctx.lineTo(50 + 50 * Math.cos(i * 2 * Math.PI / 5), 50 + 50 * Math.sin(i * 2 * Math.PI / 5));
  }
  ctx.lineWidth = 6;
  ctx.stroke();
  ctx.closePath();
  ctx.fill();
  function generateUnassignedStyle(style: any) {
    if (style.is_icon /*style.icon*/) {
        return style.icon;
    }

    let dataURL = '';
    const lineCanvas = document.createElement('canvas');
    const ctx = lineCanvas.getContext('2d')!;

    if (style.type === 'point') {
        lineCanvas.width = 20;
        lineCanvas.height = 20;

        if (style.shape === 'circle') {
            ctx.beginPath();
            ctx.fillStyle = style.fill_color;
            ctx.arc(10, 10, 8, 0, 2 * Math.PI);
            ctx.fill();
        } else if (style.shape === 'triangle') {
            lineCanvas.width = 100;
            lineCanvas.height = 100;
            ctx.beginPath();
            ctx.moveTo(50, 0);
            ctx.lineTo(0, 100);
            ctx.lineTo(100, 100);
            ctx.closePath();
            ctx.fillStyle = style.fill_color;
            ctx.lineWidth = style.stroke_width;
            ctx.strokeStyle = style.stroke_color;
            ctx.fill();
        } else if (style.shape === 'square') {
            lineCanvas.width = 100;
            lineCanvas.height = 100;
            ctx.clearRect(0, 0, 100, 100);
            ctx.beginPath();
            ctx.lineWidth = style.stroke_width;
            ctx.strokeRect(0, 0, 100, 100);
            ctx.rect(0, 0, 100, 100);
            ctx.fillStyle = style.fill_color;
            ctx.strokeStyle = style.stroke_color;
            ctx.closePath();
            ctx.fill();
            ctx.stroke();
        }

        dataURL = lineCanvas.toDataURL();
    } else if (style.type === 'linestring') {
        lineCanvas.width = 20;
        lineCanvas.height = 20;
        ctx.moveTo(0, 10);
        ctx.lineTo(20, 10);
        ctx.lineWidth = style.stroke_width;
        ctx.strokeStyle = style.stroke_color;
        ctx.stroke();
        dataURL = lineCanvas.toDataURL();
    } else if (style.type === 'polygon') {
         
        const polygonCtx = poli.getContext('2d')!;
        polygonCtx.fillStyle = style.fill_color;
        polygonCtx.strokeStyle = style.stroke_color;
        polygonCtx.lineWidth = style.stroke_width;
        
        polygonCtx.fill();
        dataURL = poli.toDataURL();
    }

    return dataURL;
}

  return (
    <div className='Sidebar non-printable'>
      <ContextMenu model={contextMenuItems} ref={contextMenu} />
      <ContextMenu model={contextMenuItemsSwipe} ref={contextMenuSwipe} />
      <Tree
        value={nodes}
        expandedKeys={expandedKeys}
        nodeTemplate={nodeTemplate}
        onToggle={e => setExpandedKeys(e.value)}
        selectionKeys={selectedKeys}
        selectionMode='checkbox'
        contextMenuSelectionKey={selectedNodeKey}
        onContextMenuSelectionChange={(e) =>{
          setSelectedNodeKey(e.value)
        }}
        onContextMenu={(e) => {
          const findedproject = projects.find((project: any) => project.name === e.node.key)
          if(findedproject){
            setContextMenuSelectedItemSwipe(findedproject)
            contextMenuSwipe.current!.layer = e.node.data;
            contextMenuSwipe.current!.show(e.originalEvent)
          }else{
            // contextMenu.current!.hide(e.originalEvent)
            projects.forEach((project: Project) => {
              project.layer_groups.forEach((layerGroup: LayerGroup) => {
                const finded = layerGroup.layers.find((layerGroup: any) => layerGroup.name === e.node.key)
                if (finded) {
                  setContextMenuSelectedItem(finded);
                  contextMenu.current!.layer = e.node.data;
                  contextMenu.current!.show(e.originalEvent)
                }
              })
            })
          }
        }}
        onSelectionChange={e => {
          setSelectedKeys(e.value)
        }}
        onNodeClick={(e: any) => {
          setSelectedGroupAlias(e.node.key);
          e.originalEvent.preventDefault();
          dispatch(setEnabledGroupAlias(e.node.key));
        }}
      />
      <Toast ref={toast} position='bottom-center' style={{ zIndex: '9 !important' }} />
    </div>
  )

}